import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TourContextValue, TourId, TourState, TourStep } from './types';
import { TOURS } from './tours';
import { TourSpotlight } from './TourSpotlight';
import { TourTooltip } from './TourTooltip';
import { TOUR_CONFIG } from './constants';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'nep_onboarding_tours';

const TourContext = createContext<TourContextValue | null>(null);

// Store references to startTour for direct calls outside of React
let startTourRef: ((tourId: TourId) => void) | null = null;
let forceStartTourRef: ((tourId: TourId) => void) | null = null;

export function startTourDirectly(tourId: TourId) {
  if (startTourRef) {
    startTourRef(tourId);
  } else {
    logger.warn('[Tour] startTourDirectly called before TourProvider mounted');
  }
}

/**
 * Force starts a tour, bypassing the completedTours check.
 * Use this after resetTour to avoid race conditions.
 */
export function forceStartTourDirectly(tourId: TourId) {
  if (forceStartTourRef) {
    forceStartTourRef(tourId);
  } else {
    logger.warn('[Tour] forceStartTourDirectly called before TourProvider mounted');
  }
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
}

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { user } = useAuth();
  const [state, setState] = useState<TourState>({
    activeTour: null,
    currentStepIndex: 0,
    completedTours: {},
  });

  // 25/09/2026 (grill com o Astra): a chave era uma só por aparelho, e a conta
  // que entrava depois herdava os tours já vistos pela anterior. Agora é por conta,
  // e trocar de conta zera o estado antes de carregar.
  // Astra r3: tentativas agendadas antes da troca de conta não reabrem o tour.
  const generationRef = useRef(0);
  const storageKey = user?.id ? `${STORAGE_KEY}:${user.id}` : STORAGE_KEY;

  // Load completed tours from localStorage and DB on mount
  useEffect(() => {
    // Astra r2: trocar de conta encerra o tour ativo e descarta resposta atrasada.
    let stale = false;
    generationRef.current += 1;
    setState(prev => ({ ...prev, completedTours: {}, activeTour: null, currentStepIndex: 0 }));
    const loadCompletedTours = async () => {
      // First check localStorage
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setState(prev => ({ ...prev, completedTours: parsed }));
        } catch (e) {
          logger.error('Failed to parse stored tours:', e);
        }
      }

      // Then sync from DB if user is logged in
      if (user?.id) {
        const { data } = await supabase
          .from('profiles')
          .select('onboarding_tours')
          .eq('id', user.id)
          .single();

        if (stale) return;
        if (data?.onboarding_tours) {
          const dbTours = data.onboarding_tours as Record<TourId, boolean>;
          setState(prev => ({
            ...prev,
            completedTours: { ...prev.completedTours, ...dbTours },
          }));
          localStorage.setItem(storageKey, JSON.stringify(dbTours));
        }
      }
    };

    loadCompletedTours();
    return () => {
      stale = true;
    };
  }, [user?.id, storageKey]);

  // Persist completed tours
  const persistCompletedTours = useCallback(async (tours: Partial<Record<TourId, boolean>>) => {
    localStorage.setItem(storageKey, JSON.stringify(tours));

    if (user?.id) {
      await supabase
        .from('profiles')
        .update({ onboarding_tours: tours })
        .eq('id', user.id);
    }
  }, [user?.id, storageKey]);

  const startTour = useCallback((tourId: TourId) => {
    if (state.completedTours[tourId]) return;

    const tour = TOURS[tourId];
    if (!tour) return;

    const generation = generationRef.current;
    const attemptStart = (retriesLeft: number) => {
      if (generation !== generationRef.current) return;
      const firstStep = tour.steps[0];
      const element = document.querySelector(firstStep.targetSelector);
      // 25/09: o tour automático não abre por cima de outra janela (anúncio,
      // instalar, suporte). Espera ela fechar; se não fechar, fica para a próxima.
      // Astra r2: o anúncio não tem data-state e a instalação não tinha role.
      const otherDialogOpen = !!document.querySelector('[role="dialog"]:not([data-state="closed"]), [role="alertdialog"]');

      if (!element || otherDialogOpen) {
        if (retriesLeft > 0) {
          setTimeout(() => attemptStart(retriesLeft - 1), TOUR_CONFIG.START_TOUR_DELAY);
          return;
        }
        logger.warn(`[Tour] ${tourId}: ${otherDialogOpen ? 'another dialog stayed open' : 'Target element not found'} after retries`);
        return;
      }

      setState(prev => ({
        ...prev,
        activeTour: tourId,
        currentStepIndex: 0,
      }));
    };

    attemptStart(TOUR_CONFIG.START_TOUR_RETRIES);
  }, [state.completedTours]);

  // Force start tour - bypasses completedTours check (for use after resetTour)
  const forceStartTour = useCallback((tourId: TourId) => {
    const tour = TOURS[tourId];
    if (!tour) return;

    const generation = generationRef.current;
    const attemptStart = (retriesLeft: number) => {
      if (generation !== generationRef.current) return;
      const firstStep = tour.steps[0];
      const element = document.querySelector(firstStep.targetSelector);

      if (!element) {
        if (retriesLeft > 0) {
          setTimeout(() => attemptStart(retriesLeft - 1), TOUR_CONFIG.START_TOUR_DELAY);
          return;
        }
        logger.warn(`[Tour] ${tourId}: Target element not found after retries (force)`);
        return;
      }

      setState(prev => ({
        ...prev,
        activeTour: tourId,
        currentStepIndex: 0,
      }));
    };

    attemptStart(TOUR_CONFIG.START_TOUR_RETRIES);
  }, []);

  // Keep the refs updated for direct calls
  useEffect(() => {
    startTourRef = startTour;
    forceStartTourRef = forceStartTour;
    return () => {
      startTourRef = null;
      forceStartTourRef = null;
    };
  }, [startTour, forceStartTour]);

  const nextStep = useCallback(() => {
    const tour = state.activeTour ? TOURS[state.activeTour] : null;
    if (!tour) return;

    let nextIndex = state.currentStepIndex + 1;

    if (nextIndex >= tour.steps.length) {
      // Tour completed
      const newCompletedTours = { ...state.completedTours, [state.activeTour]: true };
      setState(prev => ({
        ...prev,
        activeTour: null,
        currentStepIndex: 0,
        completedTours: newCompletedTours,
      }));
      persistCompletedTours(newCompletedTours);
      return;
    }

    // Find next step with existing element (skip missing ones)
    let foundValidStep = false;
    const maxSkips = 5; // Prevent infinite loop
    let skips = 0;

    while (nextIndex < tour.steps.length && skips < maxSkips) {
      const upcomingStep = tour.steps[nextIndex];
      const element = document.querySelector(upcomingStep.targetSelector);

      if (element) {
        foundValidStep = true;
        break;
      }

      nextIndex++;
      skips++;
    }

    if (!foundValidStep || nextIndex >= tour.steps.length) {
      // No valid steps found, complete the tour
      const newCompletedTours = { ...state.completedTours, [state.activeTour]: true };
      setState(prev => ({
        ...prev,
        activeTour: null,
        currentStepIndex: 0,
        completedTours: newCompletedTours,
      }));
      persistCompletedTours(newCompletedTours);
      return;
    }

    setState(prev => ({ ...prev, currentStepIndex: nextIndex }));
  }, [state.activeTour, state.currentStepIndex, state.completedTours, persistCompletedTours]);

  const prevStep = useCallback(() => {
    const tour = state.activeTour ? TOURS[state.activeTour] : null;
    if (!tour || state.currentStepIndex <= 0) return;

    let prevIndex = state.currentStepIndex - 1;
    const maxSkips = 5;
    let skips = 0;

    while (prevIndex >= 0 && skips < maxSkips) {
      const step = tour.steps[prevIndex];
      const element = document.querySelector(step.targetSelector);
      if (element) break;
      prevIndex--;
      skips++;
    }

    if (prevIndex >= 0) {
      setState(prev => ({ ...prev, currentStepIndex: prevIndex }));
    }
  }, [state.activeTour, state.currentStepIndex]);

  const skipTour = useCallback(() => {
    if (!state.activeTour) return;

    const newCompletedTours = { ...state.completedTours, [state.activeTour]: true };
    setState(prev => ({
      ...prev,
      activeTour: null,
      currentStepIndex: 0,
      completedTours: newCompletedTours,
    }));
    persistCompletedTours(newCompletedTours);
  }, [state.activeTour, state.completedTours, persistCompletedTours]);

  const endTour = useCallback(() => {
    setState(prev => ({
      ...prev,
      activeTour: null,
      currentStepIndex: 0,
    }));
  }, []);

  const isTourCompleted = useCallback((tourId: TourId) => {
    return !!state.completedTours[tourId];
  }, [state.completedTours]);

  const resetTour = useCallback(async (tourId: TourId): Promise<void> => {
    const newCompletedTours = { ...state.completedTours };
    delete newCompletedTours[tourId];
    setState(prev => ({ ...prev, completedTours: newCompletedTours }));
    await persistCompletedTours(newCompletedTours);
  }, [state.completedTours, persistCompletedTours]);

  // Get current step
  const currentStep: TourStep | null = state.activeTour
    ? TOURS[state.activeTour]?.steps[state.currentStepIndex] || null
    : null;

  const totalSteps = state.activeTour
    ? TOURS[state.activeTour]?.steps.length || 0
    : 0;

  const value: TourContextValue = {
    state,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    endTour,
    isTourCompleted,
    resetTour,
    currentStep,
    totalSteps,
  };

  return (
    <TourContext.Provider value={value}>
      {children}

      {/* Render tour overlay in portal */}
      {typeof document !== 'undefined' && state.activeTour && currentStep && createPortal(
        <>
          <TourSpotlight
            targetSelector={currentStep.targetSelector}
            padding={currentStep.spotlightPadding}
            isVisible={true}
            onClick={skipTour}
          />
          <TourTooltip
            step={currentStep}
            currentIndex={state.currentStepIndex}
            totalSteps={totalSteps}
            onNext={nextStep}
            onPrev={prevStep}
            onSkip={skipTour}
            isVisible={true}
          />
        </>,
        document.body
      )}
    </TourContext.Provider>
  );
}
